import * as React from 'react';

import type { W_BillingCode_FindForJob_VM_Single } from 'ab-api/web/billingCode/findForJob';

import type { BillingCodeVM } from 'ab-viewModels/billingCode.viewModel';
import type WorkSummaryVM from 'ab-viewModels/fieldReport/workSummary.viewModel';
import type WorkSummaryDetailsVM from 'ab-viewModels/fieldReport/workSummaryDetails.viewModel';
import type SiblingSubjobVM from 'ab-viewModels/workRequest/siblingSubjob.viewModel';

import { WorkSummaryStatus } from '@acceligentllc/shared/enums/workSummaryStatus';

import { bemElement } from 'af-utils/bem.util';
import { downloadCSV } from 'af-utils/csv.utils';
import { dollarFormatter } from 'af-utils/format.util';

import WorkSummaryBillingType from 'ab-enums/workSummaryBillingType.enum';

import DisabledFeatures from 'ab-common/environment/disabledFeatures';

import { Button } from '@acceligentllc/storybook';

import BillingTypeIndicator from '../../BillingTypeIndicator';
import { toCSVData } from '../../helpers';
import BillableAndNonBillableWorkTable from '../Table/BillableAndNonBillableWorkTable';

interface OwnProps {
	workSummaryDetails: WorkSummaryDetailsVM[];
	workSummary: WorkSummaryVM[];
	billingCodes: Record<string, W_BillingCode_FindForJob_VM_Single[]>;
	onBillingCodeSelect: (billingCode: Nullable<BillingCodeVM>, workSummaryDetailIds: number[]) => Promise<void>;
	onRowHighlight: (groupToHighlight: Nullable<string>, groupKeyToHighlight: Nullable<string>) => void;
	highlightedGroup: Nullable<string>;
	highlightedGroupKey: Nullable<string>;
	onWorkSummaryStatusChange: (wss: WorkSummaryStatus) => () => Promise<void>;
	workSummaryDetailsByGroupKey: Record<string, WorkSummaryDetailsVM[]>;
	areFRsReadOnly: boolean;
	isWorkSummaryStatusCompleted: boolean;
	isWorkSummaryStatusReviewed: boolean;
	canCompleteWorkSummary: boolean;
	areAllWorkSummariesBilled: boolean;
	workOrderCode: string;
	alternativeWorkSummary: Set<WorkSummaryVM>;
	belongsToProject: boolean;
	subJobs: SiblingSubjobVM[];
	onReallocateAll: (billingType: WorkSummaryBillingType, subJob: SiblingSubjobVM) => void;
	onBulkEditDetailBillableStatus: (workSummaryDetailIds: number[], isBillable: boolean) => void;
	handleJobReallocation: (subJob: SiblingSubjobVM, workSummaryDetailIds: number[]) => Promise<void>;
	fieldReportId: number;
	workOrderId: number;
	areMaterialsEnabled: boolean;
}

type Props = OwnProps;

const _reduceTotalBillableWork = (_acc: number, _curr: WorkSummaryVM) => {
	return _acc + (_curr.total ?? 0);
};

const WorkSummaryBillableWork: React.FC<Props> = (props) => {
	const {
		workSummaryDetails,
		workSummary,
		billingCodes,
		onBillingCodeSelect,
		onRowHighlight,
		highlightedGroup,
		onWorkSummaryStatusChange,
		canCompleteWorkSummary,
		areFRsReadOnly,
		isWorkSummaryStatusCompleted,
		isWorkSummaryStatusReviewed,
		areAllWorkSummariesBilled,
		workOrderCode,
		highlightedGroupKey,
		workSummaryDetailsByGroupKey,
		alternativeWorkSummary,
		belongsToProject,
		subJobs,
		handleJobReallocation,
		onReallocateAll,
		fieldReportId,
		workOrderId,
		onBulkEditDetailBillableStatus,
		areMaterialsEnabled,
	} = props;

	const onExportClick = React.useCallback(() => {
		const csvData = toCSVData(workSummary, belongsToProject, WorkSummaryBillingType.BILLABLE_WORK);
		downloadCSV(csvData, `${workOrderCode}_billable_work.csv`);
	}, [belongsToProject, workOrderCode, workSummary]);

	const handleBulkEditDetailBillableStatus = React.useCallback((workSummaryDetailIds: number[]) => {
		onBulkEditDetailBillableStatus(workSummaryDetailIds, false);
	}, [onBulkEditDetailBillableStatus]);

	const totalBillableWork = workSummary.reduce(_reduceTotalBillableWork, 0);
	const showHint = totalBillableWork === 0;

	const billableWorkClassName = 'field-report__work-summary__billable-work';
	const headerClassName = bemElement(billableWorkClassName, 'header');

	const isWorkSummaryReadonly = isWorkSummaryStatusCompleted || areFRsReadOnly || isWorkSummaryStatusReviewed;
	const isBillableStatusEditable = !(isWorkSummaryStatusCompleted || areFRsReadOnly);
	const isCompleteButtonDisabled = areFRsReadOnly || !areAllWorkSummariesBilled;

	return (
		<>
			<div className={billableWorkClassName}>
				<div className={bemElement(billableWorkClassName, 'legend')}>
					<span className={bemElement('field-report__work-summary__billable-work__legend', 'single')}>
						<BillingTypeIndicator
							billingType={WorkSummaryBillingType.BILLABLE_WORK}
							className={bemElement('field-report__work-summary__billable-work__legend__single', 'billing-type-indicator')}
							small={true}
						/>
						Work
					</span>
					{!DisabledFeatures.Materials && areMaterialsEnabled && (
						<span className={bemElement('field-report__work-summary__billable-work__legend', 'single')}>
							<BillingTypeIndicator
								billingType={WorkSummaryBillingType.MATERIAL_COST}
								className={bemElement('field-report__work-summary__billable-work__legend__single', 'billing-type-indicator')}
								small={true}
							/>
							Material
						</span>
					)}
					<span className={bemElement('field-report__work-summary__billable-work__legend', 'single')}>
						<span className={bemElement('field-report__work-summary__billable-work__legend__single', 'empty-cell', { selected: true })} />
						Selected
					</span>
					<span className={bemElement('field-report__work-summary__billable-work__legend', 'single')}>
						<span className={bemElement('field-report__work-summary__billable-work__legend__single', 'empty-cell', { suggested: true })} />
						Alternative billable work / material expense
					</span>
					<span className={bemElement('field-report__work-summary__billable-work__legend', 'single')}>
						<span className={bemElement('field-report__work-summary__billable-work__legend__single', 'recently-used')}>
							<span className="icon-star" />
						</span>
						Recently used
					</span>
					<span className={bemElement('field-report__work-summary__billable-work__legend', 'single')}>
						<span className={bemElement('field-report__work-summary__billable-work__legend__single', 'billable')}>
							<span className="icon-dollar" />
						</span>
						Included in calculation
					</span>
					<span className={bemElement('field-report__work-summary__billable-work__legend', 'single')}>
						<span className={bemElement('field-report__work-summary__billable-work__legend__single', 'non-billable')}>
							<span className="icon-dollar" />
						</span>
						Excluded from calculation
					</span>
				</div>
				<div className={headerClassName}>
					<BillingTypeIndicator billingType={WorkSummaryBillingType.BILLABLE_WORK} />
					<span className={bemElement(headerClassName, 'title')}>
						Billable Work
					</span>
					<Button
						disabled={!workSummary.length}
						icon="ActionDownload"
						label="Export"
						onClick={onExportClick}
						style="link"
					/>
				</div>
				<BillableAndNonBillableWorkTable
					alternativeWorkSummary={alternativeWorkSummary}
					belongsToProject={belongsToProject}
					billingCodes={billingCodes}
					billingType={WorkSummaryBillingType.BILLABLE_WORK}
					fieldReportId={fieldReportId}
					handleJobReallocation={handleJobReallocation}
					highlightedGroup={highlightedGroup}
					highlightedGroupKey={highlightedGroupKey}
					isBillable={true}
					isBillableStatusEditable={isBillableStatusEditable}
					isReadOnly={isWorkSummaryReadonly}
					onBillingCodeSelect={onBillingCodeSelect}
					onBulkEditDetailBillableStatus={handleBulkEditDetailBillableStatus}
					onReallocateAll={onReallocateAll}
					onRowHighlight={onRowHighlight}
					subJobs={subJobs}
					workOrderId={workOrderId}
					workSummary={workSummary}
					workSummaryDetails={workSummaryDetails}
					workSummaryDetailsByGroupKey={workSummaryDetailsByGroupKey}
				/>
				<div className={bemElement('field-report__work-summary__billable-work', 'total')}>
					{showHint && (
						<span className={bemElement('field-report__work-summary__billable-work__total', 'hint')}>
							Please add a billing code to at least one row to calculate the total
						</span>
					)}
					{`Total ${dollarFormatter.format(totalBillableWork)}`}
				</div>
				<div className={bemElement(billableWorkClassName, 'review-actions')}>
					{isWorkSummaryStatusReviewed && canCompleteWorkSummary && (
						<Button
							disabled={isCompleteButtonDisabled}
							icon="Check"
							label="Complete"
							onClick={onWorkSummaryStatusChange(WorkSummaryStatus.COMPLETED)}
							style="secondary"
						/>
					)}
					{!(isWorkSummaryStatusReviewed || isWorkSummaryStatusCompleted) && (
						<Button
							disabled={isWorkSummaryReadonly}
							icon="ReviewVisilibity"
							label="Review"
							onClick={onWorkSummaryStatusChange(WorkSummaryStatus.REVIEWED)}
							style="secondary"
						/>
					)}
					{!areFRsReadOnly && (isWorkSummaryStatusReviewed || isWorkSummaryStatusCompleted) &&
						<Button
							icon="ActionEdit"
							label="Edit"
							onClick={onWorkSummaryStatusChange(WorkSummaryStatus.OUTDATED)}
							style="secondary"
						/>
					}
				</div>
			</div>
		</>
	);
};

export default React.memo(WorkSummaryBillableWork);
